import type { ThemeConfig } from "antd/es/config-provider/context";

// shadcn-style structure (flat, hairline borders, soft shadows) coloured with the
// core frontend's tokens: slate neutrals + orange reserved for calls to action.
export const palette = {
  brand: "#1e293b", // slate-800
  brandHover: "#334155", // slate-700
  brandPressed: "#0f172a", // slate-900
  cta: "#f97316", // orange-500
  ctaHover: "#ea580c", // orange-600
  ctaPressed: "#c2410c", // orange-700
  ctaDisabled: "#fed7aa", // orange-200
  page: "#f1f5f9", // slate-100
  card: "#ffffff",
  cardSecondary: "#fbfcfd", // slate-25
  modal: "#e9eef4", // slate-150
  infoTag: "#d6dee8", // slate-250
  headers: "#0f172a", // slate-900
  body: "#475569", // slate-600
  meta: "#334155", // slate-700
  support: "#64748b", // slate-500
  disabled: "#94a3b8", // slate-400
  stroke: "#d6dee8", // slate-250
  strokeEmphasis: "#64748b", // slate-500
  success: "#22c55e",
  successText: "#15803d",
  successSurface: "#f0fdf4",
  danger: "#ef4444",
  dangerText: "#b91c1c",
  dangerSurface: "#fee2e2",
  highlightSurface: "#ffedd5", // orange-100
  highlightText: "#c2410c", // orange-700
};

export const FONT_FAMILY =
  "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export const shadcnTheme: ThemeConfig = {
  token: {
    fontFamily: FONT_FAMILY,
    fontSize: 14,

    colorPrimary: palette.brand,
    colorPrimaryHover: palette.brandHover,
    colorPrimaryActive: palette.brandPressed,
    colorPrimaryBg: palette.modal,
    colorPrimaryBgHover: palette.infoTag,
    colorPrimaryBorder: "#cbd5e1",
    colorPrimaryBorderHover: palette.disabled,
    colorInfo: palette.brand,
    colorInfoBg: palette.cardSecondary,
    colorInfoBorder: palette.stroke,
    colorInfoText: palette.meta,
    colorSuccess: palette.success,
    colorSuccessBg: palette.successSurface,
    colorSuccessText: palette.successText,
    colorSuccessBorder: "#bbf7d0",
    colorWarning: palette.cta,
    colorWarningBg: palette.highlightSurface,
    colorWarningText: palette.highlightText,
    colorWarningBorder: palette.ctaDisabled,
    colorError: palette.danger,
    colorErrorBg: palette.dangerSurface,
    colorErrorText: palette.dangerText,
    colorErrorBorder: "#fecaca",
    colorLink: palette.brand,
    colorLinkHover: palette.cta,

    colorText: palette.meta,
    colorTextHeading: palette.headers,
    colorTextSecondary: palette.support,
    colorTextTertiary: palette.disabled,
    colorTextPlaceholder: palette.disabled,
    colorTextDisabled: palette.disabled,

    colorBgLayout: palette.page,
    colorBgContainer: palette.card,
    colorBorder: palette.stroke,
    colorBorderSecondary: palette.stroke,
    colorSplit: palette.stroke,
    colorFillAlter: palette.cardSecondary,

    borderRadius: 8,
    borderRadiusSM: 4,
    borderRadiusLG: 12,

    controlHeight: 36,
    controlOutline: "rgba(249, 115, 22, 0.2)",
    controlOutlineWidth: 3,

    boxShadow: "0 1px 2px 0 rgba(15, 23, 42, 0.05)",
    boxShadowSecondary:
      "0 4px 6px -1px rgba(15, 23, 42, 0.1), 0 2px 4px -2px rgba(15, 23, 42, 0.1)",
  },
  components: {
    Button: {
      colorPrimaryHover: palette.brandHover,
    },
    Card: {
      colorBorderSecondary: palette.stroke,
      boxShadow: "0 1px 2px 0 rgba(15, 23, 42, 0.05)",
    },
    Table: {
      colorBgContainer: palette.card,
      colorFillAlter: palette.cardSecondary,
      colorBorderSecondary: palette.stroke,
      colorFillContent: palette.page,
    },
    Menu: {
      colorItemBg: "transparent",
      colorItemBgSelected: palette.infoTag,
      colorItemTextSelected: palette.headers,
      colorItemText: palette.support,
      colorItemTextHover: palette.headers,
      colorItemBgHover: palette.page,
    },
    Tag: {
      colorFillAlter: palette.infoTag,
    },
  },
};
